import { Button, Divider, InputAdornment } from "@mui/material";
import { useEffect, useState } from "react";

import ErrorModal from "../../components/Layout/ErrorModal";
import FlagUs from "../../assets/Icons/FlagUs";
import Mail from "../../assets/Icons/Mail";
import Message from "../../assets/Icons/Message";
import Modal from "../../components/Layout/Modal";
import PageLayout from "../../components/Layout/PageLayout";
import TextField from "../../components/Inputs/TextField";
import { normalizeLicensePlate } from "../../utils/formatLicensePlate";
import { normalizePhoneNumber } from "../../utils/formatPhone";
import { postEmail } from "../../services/email";
import { postPlateUpdate } from "../../services/plate";
import { postReceipt } from "../../services/receipt";
import { postText } from "../../services/text";
import { useNavigate } from "react-router-dom";
import { useReceiptLink } from "../../hooks/receipt-link";
import { useSearchParams } from "react-router-dom";
import validateEmail from "../../utils/validate-email";
import validatePhoneNumber from "../../utils/validate-phone";

const ReceiptLink = () => {
  const TEXT_ERROR =
    "An error occurred sending your text receipt. Please try again or contact support if the problem persists.";
  const EMAIL_ERROR =
    "An error occurred sending your email receipt. Please try again or contact support if the problem persists.";
  const RECEIPT_ERROR =
    "An error occurred retrieving your receipt. Please try again or contact support if the problem persists.";

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sendPhoneReceipt, setSendPhoneReceipt] = useState(false);
  const [sendEmailReceipt, setSendEmailReceipt] = useState(false);
  const [sendReceiptIsLoading, setSendReceiptIsLoading] = useState(false);
  const [receiptError, setReceiptError] = useState(null);
  const [plateError, setPlateError] = useState(null);

  const [fixLicensePlate, setFixLicensePlate] = useState(false);
  const [plateUpdateIsLoading, setPlateUpdateIsLoading] = useState(false);
  const [newPlate, setNewPlate] = useState("");

  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [emailWarning, setEmailWarning] = useState("");
  const [phoneWarning, setPhoneWarning] = useState("");
  const receiptId = searchParams.get("receipt");
  const {
    data,
    isLoading,
    error: receiptLinkError,
  } = useReceiptLink(receiptId);

  useEffect(() => {
    if (!sendEmailReceipt || email === "") return;
    setEmailWarning(
      validateEmail(email) ? "" : "Please enter a valid email address."
    );
  }, [sendEmailReceipt, email]);

  useEffect(() => {
    if (!sendPhoneReceipt || phoneNumber === "") return;
    setPhoneWarning(
      validatePhoneNumber(phoneNumber)
        ? ""
        : "Please enter a valid mobile phone number."
    );
  }, [sendPhoneReceipt, phoneNumber]);

  const handleOnChangePhone = (e) => {
    setPhoneNumber(normalizePhoneNumber(e.target.value));
  };
  const handleOnChangeEmail = (e) => {
    setEmail(e.target.value);
  };

  const handleSendReceipt = async () => {
    let op;

    try {
      if (sendPhoneReceipt) {
        setSendReceiptIsLoading(true);
        op = "TEXT";
        await postText(data?.receipt?.receiptID, phoneNumber);
      }
      if (sendEmailReceipt) {
        setSendReceiptIsLoading(true);
        op = "EMAIL";
        await postEmail(data?.receipt?.receiptID, email);
      }
    } catch (error) {
      setReceiptError(
        op === "TEXT"
          ? TEXT_ERROR
          : op === "EMAIL"
          ? EMAIL_ERROR
          : RECEIPT_ERROR
      );
    } finally {
      setSendReceiptIsLoading(false);
      setSendEmailReceipt(false);
      setSendPhoneReceipt(false);
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.toLocaleString("default", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    })}`;
  };

  const formatTime = (date) => {
    const d = new Date(date);
    return `${d.toLocaleString("default", {
      month: "short",
      day: "2-digit",
      hour: "numeric",
      minute: "numeric",
      timeZoneName: "short",
    })}`;
  };

  const goodToSend =
    (sendPhoneReceipt && validatePhoneNumber(phoneNumber)) ||
    (sendEmailReceipt && validateEmail(email));

  const handleOnChangePlate = (e) => {
    const changedPlate = e.target.value;
    const plate = changedPlate.substring(0, 18).toUpperCase();
    setNewPlate(normalizeLicensePlate(plate));
  };

  const handleOnChangePlateSubmit = async () => {
    setFixLicensePlate(false);
    setPlateUpdateIsLoading(true);
    try {
      const upadateRes = await postPlateUpdate(
        data.receipt.receiptID,
        newPlate,
        data.receipt.plate
      );
      const receiptRes = await postReceipt(upadateRes?.receipt?.receiptID);
      data.receipt.plate = receiptRes?.receipt?.plate;
      data.receipt.lotName = receiptRes?.receipt?.lotName;
      data.allowPlateSwitch = receiptRes.allowPlateSwitch;
      setPlateUpdateIsLoading(false);
    } catch (e) {
      setPlateUpdateIsLoading(false);
      setPlateError(e.message);
    }
  };

  return (
    <PageLayout
      logoReplacement={data?.receipt?.locationName}
      title="Receipt"
      isLoading={isLoading || sendReceiptIsLoading || plateUpdateIsLoading}
      hideHelpButton={true}
    >
      <ErrorModal
        description={receiptError ?? RECEIPT_ERROR}
        open={!!receiptError || !!receiptLinkError}
        onClose={() => {
          setReceiptError(null);
          if (receiptLinkError) navigate(-1);
        }}
      />
      <ErrorModal
        failReason={plateError}
        open={!!plateError}
        onClose={() => {
          setPlateError(null);
        }}
      />
      {data?.receipt.accessCode && (
        <div
          className="rounded flex flex-col p-4"
          style={{ backgroundColor: "var(--background-color)" }}
        >
          <h2 className="font-bold">Door Access Code</h2>
          <div className="mt-2">
            Please save the following code. It will be required to gain access
            to this facility.
          </div>
          <div className="text-4xl font-bold mt-2">
            {data.receipt.accessCode}
          </div>
        </div>
      )}
      <div className="text-secondary">
        <div className="divider">
          <div style={{ fontWeight: 700 }}></div>
        </div>
        <div className="text-left mt-4">
          <div className="font-bold">Location Details</div>

          <div>
            <div className="mt-1">
              <span>Location:</span>
              <span className="float-right">{data?.receipt?.locationName}</span>
            </div>
            <div className="mt-1">
              <span>Date:</span>
              <span className="float-right">
                {formatDate(data?.receipt?.startTime)}
              </span>
            </div>
          </div>
        </div>
        <div className="divider mt-4">
          <div style={{ fontWeight: 700 }}></div>
        </div>
        <div className="text-left mt-4">
          <div className="font-bold">Vehicle</div>
          <div>
            <div className="mt-1">
              <span>License Plate Number:</span>
              <span className="float-right">{data?.receipt?.plate}</span>
            </div>
          </div>
          {data?.allowPlateSwitch ? (
            <Button
              size="large"
              variant="contained"
              onClick={() => setFixLicensePlate(true)}
              color="neutral"
              style={{
                fontWeight: 700,
                width: "100%",
                height: "3rem",
                marginTop: "1rem",
              }}
            >
              Incorrect License Plate?
            </Button>
          ) : null}
        </div>
        <div className="divider mt-4">
          <div style={{ fontWeight: 700 }}></div>
        </div>
        <div className="text-left mt-4">
          <div className="font-bold">Parking Pass Information</div>
          <div>
            <div className="mt-1">
              <span>Lot:</span>
              <span className="float-right">{data?.receipt?.lotName}</span>
            </div>
            <div className="mt-1">
              <span>Activation Time:</span>
              <span className="float-right">
                {formatTime(data?.receipt?.startTime)}
              </span>
            </div>
            <div className="mt-1">
              <span>End Time:</span>
              <span className="float-right">
                {formatTime(data?.receipt?.endTime)}
              </span>
            </div>
          </div>
        </div>
        <div className="divider mt-4">
          <div style={{ fontWeight: 700 }}></div>
        </div>
        <div className="text-left mt-4">
          <div className="font-bold">Payment Details</div>
          <div>
            <div className="mt-1">
              <span>Invoice Number:</span>
              <span className="float-right">{data?.receipt?.invoiceID}</span>
            </div>
            <div className="mt-1">
              <span>Payment Method:</span>
              <span className="float-right">
                {data?.receipt?.paymentDescription}
              </span>
            </div>
            <div className="mt-1">
              <span>Discount:</span>
              <span className="float-right">
                {typeof data?.receipt?.discount === "number" &&
                !isNaN(data.receipt.discount) &&
                data.receipt.discount > 0
                  ? `-$${data.receipt?.discount
                      .toFixed(2)
                      .replace(/\.00$/, "")}`
                  : "--"}
              </span>
            </div>
            <div className="mt-1">
              <span>Total Paid:</span>
              <span className="float-right">
                $
                {typeof data?.receipt?.amountPaid === "number" &&
                !isNaN(data.receipt.amountPaid)
                  ? data?.receipt.amountPaid.toFixed(2).replace(/\.00$/, "")
                  : "0"}
              </span>
            </div>
          </div>
        </div>
      </div>
      <Button
        size="large"
        variant="contained"
        onClick={() => setSendPhoneReceipt(true)}
        color="neutral"
        style={{
          fontWeight: 700,
          width: "100%",
          height: "3rem",
          marginTop: "1rem",
        }}
        startIcon={<Message fill="black" />}
      >
        Send Receipt via Text
      </Button>
      <div className="mt-4">
        <Button
          size="large"
          variant="contained"
          onClick={() => setSendEmailReceipt(true)}
          color="neutral"
          style={{ fontWeight: 700, width: "100%", height: "3rem" }}
          startIcon={<Mail fill="black" />}
        >
          Send Receipt via Email
        </Button>
      </div>
      <Modal
        open={sendPhoneReceipt || sendEmailReceipt}
        withClose
        title={`Send receipt via ${sendPhoneReceipt ? "text" : "email"}`}
        onClose={() => {
          setSendPhoneReceipt(false);
          setSendEmailReceipt(false);
        }}
      >
        <div className="mt-8">
          <div style={{ marginLeft: "-1.5rem", marginRight: "-1.5rem" }}>
            <Divider />
          </div>
          {sendPhoneReceipt && (
            <div className="my-8">
              <TextField
                value={phoneNumber}
                onChange={(e) => handleOnChangePhone(e)}
                label="Phone Number"
                type="tel"
                placeholder="(123) 456-7890"
                error={phoneWarning}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FlagUs />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          )}
          {sendEmailReceipt && (
            <div className="my-5">
              <TextField
                value={email}
                type="email"
                onChange={(e) => handleOnChangeEmail(e)}
                label="Email"
                placeholder="john.doe@example.com"
                error={emailWarning}
              />
            </div>
          )}
        </div>
        <div style={{ marginLeft: "-1.5rem", marginRight: "-1.5rem" }}>
          <Divider />
        </div>
        <div>
          <div className="mt-4 flex justify-end">
            <Button
              size="medium"
              variant="contained"
              onClick={() => handleSendReceipt()}
              color="primary"
              disabled={!goodToSend}
              style={{
                fontWeight: 700,
                height: "2.5rem",
                width: "45%",
              }}
            >
              Send Receipt
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        open={fixLicensePlate}
        withClose
        title="Update Plate"
        onClose={() => setFixLicensePlate(false)}
      >
        <div style={{ marginLeft: "-.5rem" }}>
          <div className="mt-8">
            <div className="mt-4">
              Did you checkout with the wrong license plate? No worries, you can
              still update it.
            </div>
            <div className="mt-4 justify-end">
              <TextField
                id="license-plate"
                value={newPlate}
                onChange={(e) => handleOnChangePlate(e)}
                label="License Plate"
                placeholder="ABC123"
              />
            </div>
            <div
              style={{
                marginTop: "1rem",
                marginLeft: "-1rem",
                marginRight: "-1.5rem",
              }}
            >
              <Divider />
            </div>
          </div>
          <div>
            <div className="mt-4 flex justify-end">
              <Button
                size="large"
                variant="contained"
                disabled={newPlate.length < 2}
                onClick={() => handleOnChangePlateSubmit()}
                color="primary"
                style={{
                  fontWeight: 700,
                  width: "100%",
                  height: "3rem",
                }}
              >
                Update License Plate
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </PageLayout>
  );
};

export default ReceiptLink;
